import { Patient, Procedure } from '@medplum/fhirtypes';
import { FC } from 'react';
import { getDisplayString } from '@medplum/core';
import { ResourceListSection } from '../shared/ResourceListSection';
import { ResourceListSectionConfig } from '../shared/ResourceListSectionConfig';
import { TherapeuticServiceListItem } from '../../../examination/therapeuticServices/TherapeuticServiceListItem';
import { mapProcedureToTherapeuticService } from '@/utils/therapeutic-service.utils';
import { createActiveFilter } from '../shared/resource-sort-utils';

// -----------------------------------------------------------------------------
export interface TherapeuticInterventionsProps {
  readonly patient: Patient;
  readonly therapeuticInterventions: Procedure[];
}

// -----------------------------------------------------------------------------
const getPerformedDate = (procedure: Procedure): string => {
  return procedure.performedPeriod?.start || procedure.performedDateTime || '';
};

// -----------------------------------------------------------------------------
const createTherapeuticInterventionConfig = (
  patient: Patient
): ResourceListSectionConfig<Procedure> => ({
  titleKey: 'summaryPage.summary.therapeuticInterventions.title',
  noneKey: 'summaryPage.summary.therapeuticInterventions.none',
  noResourcesKey:
    'summaryPage.summary.therapeuticInterventions.therapeuticInterventionDisplay.noTherapeuticInterventions',

  sortResources: (procedures) => {
    const inProgressFilter = createActiveFilter<Procedure>(
      (p) => p.status,
      'in-progress'
    );
    return inProgressFilter(procedures).sort((a, b) =>
      getPerformedDate(b).localeCompare(getPerformedDate(a))
    );
  },

  getDisplayDate: getPerformedDate,

  getDisplayName: (procedure) =>
    procedure.code?.coding?.[0]?.display ||
    procedure.code?.text ||
    getDisplayString(procedure),

  getStatusLabel: (procedure, t) =>
    t(
      `summaryPage.summary.therapeuticInterventions.dialog.statusOptions.${procedure.status}`,
      {
        defaultValue: procedure.status,
      }
    ),

  renderItem: (procedure) => (
    <TherapeuticServiceListItem
      service={mapProcedureToTherapeuticService(procedure)}
      patient={patient}
      hideActions
    />
  ),
});

// -----------------------------------------------------------------------------
export const TherapeuticInterventions: FC<TherapeuticInterventionsProps> = ({
  patient,
  therapeuticInterventions,
}) => {
  return (
    <ResourceListSection<Procedure>
      resources={therapeuticInterventions}
      config={createTherapeuticInterventionConfig(patient)}
    />
  );
};
